import React, { useState } from 'react';
import _get from 'lodash/get';
import Assessments from './Assessments/index.js';
import Progress from './Progress/index.js';
import Notes from './Notes/index.js';
import Goals from './Goals/index.js';
import TrackingReport from './TrackingReport';
import IconPin from 'cccisd-icons/pushpin';
import IconDown from 'cccisd-icons/arrow-down20';
import IconUp from 'cccisd-icons/arrow-up20';
import IconMessage from 'cccisd-icons/envelop2';
import IconCheck from 'cccisd-icons/checkmark4';
import IconFamily from 'cccisd-icons/users4';
import style from './style.css';

/*
NOTES FROM DEMO:
- "Notes" is a single button at the family level, but it opens a
    modal with several textboxes (1 per famiily member + 1 for entire family)
- "Fidelity Checklist" opens new checklist in a different tab, so Coach can
    view data on their dashboard during in-person session
- Fidelity Checklist is a repeatable survey. Will likely be 1 per lesson
    in the course, but not necessarily. This button should open a modal
    with an option to "Start New Fidelity Checklist", or a table to see past
    submissions. Table columns are row number, lesson (metric), and link to
    see PDF or responses
*/

function Family({ family, isPinned, togglePinned, familyName, getMemberName }) {
    const [isExpanded, setIsExpanded] = useState(false);

    const familyGroupId = _get(family, 'group.groupId');

    const adults = _get(family, 'descendantRoles.tfhParentList', []) || [];
    const children = _get(family, 'descendantRoles.tfhChildList', []) || [];
    const allFamilyMembers = [...adults, ...children];

    return (
        <div key={familyGroupId} className={style.familyWrapper}>
            <button
                className={`btn btn-sm ${isPinned ? 'btn-primary' : 'btn-outline-primary'} ${style.pin}`}
                onClick={() => togglePinned(family)}
                type="button"
            >
                <IconPin />
            </button>
            <div className={style.collapsedContent}>
                <div className={style.familyLabel}>
                    <small>
                        <i>Family of</i>
                    </small>
                    &nbsp;&nbsp;
                    <span className="fs-3">{familyName}</span>
                </div>
                <div className={style.actions}>
                    <Notes family={family} familyName={familyName} />
                    <Assessments
                        allFamilyMembers={allFamilyMembers}
                        family={family}
                        familyName={familyName}
                        getMemberName={getMemberName}
                    />
                    <Progress family={family} familyName={familyName} />
                    <button type="button" className="btn btn-sm btn-secondary">
                        <IconCheck spaceRight />
                        Session Checklist
                    </button>
                    <button type="button" className="btn btn-sm btn-secondary">
                        <IconFamily spaceRight />
                        Profile
                    </button>
                </div>
                <div className={style.expandButton}>
                    <button type="button" className="btn btn-sm btn-link" onClick={() => setIsExpanded(prev => !prev)}>
                        {isExpanded ? 'Less' : 'More'}
                        {isExpanded ? <IconUp spaceLeft /> : <IconDown spaceLeft />}
                    </button>
                </div>
            </div>
            {isExpanded && (
                <div className={style.expandedContent}>
                    <table className={'table table-sm ' + style.membersTable}>
                        <thead>
                            <tr>
                                <th scope="col">Name</th>
                                <th scope="col">Role</th>
                                <th scope="col">Last Login</th>
                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allFamilyMembers.map(member => {
                                const memberDisplayName = getMemberName(member);
                                return (
                                    <tr key={member.pawn.pawnId}>
                                        <td>{memberDisplayName}</td>
                                        <td>{member.role.handle.replace(/^tfh/i, '')}</td>
                                        <td>{member.user.lastLoginDate || '-'}</td>
                                        <td className={style.actions}>
                                            <Goals member={member} memberDisplayName={memberDisplayName} />
                                            <TrackingReport member={member} memberDisplayName={memberDisplayName} />
                                            {member.role.handle === 'tfhParent' && (
                                                <button className="btn btn-sm btn-secondary" type="button">
                                                    <IconMessage spaceRight />
                                                    Message
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default Family;
